"use client";

import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@ui/components/alert-dialog";
import { Button } from "@ui/components/button";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
	useState,
} from "react";

type ConfirmOptions = {
	title: string;
	message?: string;
	cancelLabel?: string;
	confirmLabel?: string;
	destructive?: boolean;
	onConfirm: () => Promise<void> | void;
};

const ConfirmationAlertContext = createContext<{
	confirm: (options: ConfirmOptions) => void;
} | null>(null);

/**
 * One confirmation dialog for the app. The confirm button shows progress and
 * the dialog stays open until `onConfirm` settles, so a slow delete can't be
 * clicked twice or dismissed half-way.
 */
export function ConfirmationAlertProvider({ children }: PropsWithChildren) {
	const [options, setOptions] = useState<ConfirmOptions | null>(null);
	const [pending, setPending] = useState(false);
	const value = useMemo(() => ({ confirm: setOptions }), []);

	return (
		<ConfirmationAlertContext.Provider value={value}>
			{children}

			<AlertDialog
				open={!!options}
				onOpenChange={(open) => {
					if (!open && !pending) {
						setOptions(null);
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{options?.title}</AlertDialogTitle>
						{options?.message ? (
							<AlertDialogDescription>
								{options.message}
							</AlertDialogDescription>
						) : null}
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={pending}>
							{options?.cancelLabel ?? "Cancel"}
						</AlertDialogCancel>
						<Button
							variant={
								options?.destructive ? "destructive" : "default"
							}
							disabled={pending}
							onClick={async () => {
								if (!options) {
									return;
								}
								setPending(true);
								try {
									await options.onConfirm();
									setOptions(null);
								} catch (error) {
									toast.add({
										type: "error",
										title: "That didn't work",
										description:
											error instanceof Error
												? error.message
												: undefined,
									});
								} finally {
									setPending(false);
								}
							}}
						>
							{pending ? (
								<Spinner data-icon="inline-start" />
							) : null}
							{options?.confirmLabel ?? "Confirm"}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</ConfirmationAlertContext.Provider>
	);
}

export const useConfirmationAlert = () => {
	const context = useContext(ConfirmationAlertContext);
	if (!context) {
		throw new Error(
			"useConfirmationAlert must be used within a ConfirmationAlertProvider",
		);
	}
	return context;
};
